import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { FaMapMarkerAlt, FaSearch, FaTimes } from 'react-icons/fa';
import { AppDispatch } from '../../redux/Store';
import { searchLocations, Location } from '../../redux/thunks/locationThunks';
import './LocationPicker.scss';

interface LocationPickerProps {
  label: string;
  placeholder?: string;
  value: Location | null;
  onChange: (location: Location | null) => void;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
}

/**
 * LocationPicker component for searching and selecting locations
 * Used in profile creation and settings
 */
export const LocationPicker: React.FC<LocationPickerProps> = ({
  label,
  placeholder = 'Search for a location...',
  value,
  onChange,
  disabled = false,
  required = false,
  helperText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Location[]>([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Set initial search query from value
  useEffect(() => {
    if (value && value.locationText) {
      setSearchQuery(value.locationText);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback(
    async (query: string) => {
      setSearchQuery(query);
      setDropdownOpen(true);

      if (query.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const result = await dispatch(searchLocations({ query, limit: 10 })).unwrap();
        setSearchResults(result.locations);
      } catch (error) {
        console.error('Location search failed:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [dispatch]
  );

  const handleLocationSelect = (location: Location) => {
    onChange(location);
    setSearchQuery(location.locationText || '');
    setDropdownOpen(false);
  };

  const handleClearLocation = () => {
    onChange(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="location-picker" ref={dropdownRef}>
      <label className="location-picker-label">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>

      <div className="location-picker-input-wrapper">
        <FaMapMarkerAlt className="location-picker-icon" />
        <input
          type="text"
          className="location-picker-input"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => searchQuery.length >= 2 && setDropdownOpen(true)}
          disabled={disabled}
        />
        {value && !disabled && (
          <button
            type="button"
            className="location-picker-clear"
            onClick={handleClearLocation}
            aria-label="Clear location"
          >
            <FaTimes />
          </button>
        )}
        {searching && (
          <div className="location-picker-spinner" />
        )}
      </div>

      {helperText && <p className="location-picker-helper">{helperText}</p>}

      {dropdownOpen && searchResults.length > 0 && (
        <div className="location-picker-dropdown">
          {searchResults.map((location) => (
            <button
              key={location.id}
              type="button"
              className="location-picker-option"
              onClick={() => handleLocationSelect(location)}
            >
              <FaMapMarkerAlt className="location-option-icon" />
              <div className="location-option-text">
                <div className="location-option-name">{location.locationText}</div>
                {location.country && (
                  <div className="location-option-country">{location.country}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {dropdownOpen && !searching && searchQuery.length >= 2 && searchResults.length === 0 && (
        <div className="location-picker-dropdown">
          <div className="location-picker-no-results">
            <FaSearch />
            <p>No locations found</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
